import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT, PROJECT_TYPES, BUDGETS, TIMELINES } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  neighborhood: "",
  project_type: "",
  budget_range: "",
  timeline: "",
  message: "",
  website: "", // honeypot — must stay empty
};

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#262626] focus:border-champagne focus:ring-1 focus:ring-[#C5A059] text-[#F5F5F0] placeholder:text-[#525252] rounded-sm px-4 py-3.5 outline-none transition-all font-sans-body text-[15px]";

export default function EstimateForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.project_type) {
      toast.error("Please fill in name, email, phone and project type.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(initial);
      toast.success("Estimate request received. We'll be in touch within 1 business day.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="py-24 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: trust + contact */}
        <div className="lg:col-span-5 reveal">
          <span className="overline">Free estimate</span>
          <h2 className="font-serif-display mt-5 text-4xl sm:text-5xl md:text-6xl text-[#F5F5F0] leading-tight">
            Tell us about<br /><span className="italic text-champagne">your bathroom.</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3] leading-relaxed max-w-md font-sans-body">
            A real person — usually the owner — reads every request. We'll reply within one business day with next steps,
            an on-site visit, and a fixed-price quote.
          </p>

          <div className="mt-12 space-y-6">
            <ContactRow icon={Phone} label="Call directly" value={CONTACT.phone} href={CONTACT.phoneHref} testid="contact-phone" />
            <ContactRow icon={Mail} label="Email" value={CONTACT.email} href={CONTACT.emailHref} testid="contact-email" />
            <ContactRow icon={MapPin} label="Service area" value={CONTACT.area} testid="contact-area" />
            <ContactRow icon={Clock} label="Hours" value={CONTACT.hours} testid="contact-hours" />
          </div>

          <div className="mt-12 pt-10 border-t border-white/5 grid grid-cols-2 gap-6 text-[12px] tracking-[0.18em] uppercase text-[#A3A3A3] font-sans-body">
            <div>Licensed & Insured</div>
            <div>WSIB Compliant</div>
            <div>5-Year Warranty</div>
            <div>Fixed-Price Quotes</div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="estimate-success"
              className="border border-[#262626] rounded-sm bg-[#0C0C0C] p-10 md:p-14 text-center"
            >
              <CheckCircle2 className="w-12 h-12 text-champagne mx-auto" />
              <h3 className="font-serif-display text-3xl md:text-4xl mt-6 text-[#F5F5F0]">Request received.</h3>
              <p className="mt-4 text-[#A3A3A3] font-sans-body max-w-md mx-auto">
                Thanks — we'll be in touch within one business day. For anything urgent,
                give us a call at <a href={CONTACT.phoneHref} className="text-champagne underline-grow">{CONTACT.phone}</a>.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="estimate-send-another"
                className="mt-8 text-[12px] tracking-[0.22em] uppercase text-[#A3A3A3] hover:text-champagne font-sans-body"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="estimate-form"
              className="border border-[#262626] rounded-sm bg-[#0C0C0C] p-6 md:p-10 space-y-5"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name *">
                  <input
                    data-testid="form-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Tremblay"
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label="Email *">
                  <input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@example.com"
                    className={inputCls}
                    required
                  />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Phone *">
                  <input
                    data-testid="form-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(613) 555-0123"
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label="Neighborhood">
                  <input
                    data-testid="form-neighborhood"
                    value={form.neighborhood}
                    onChange={set("neighborhood")}
                    placeholder="Glebe, Kanata, Orléans…"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Project type *">
                <select
                  data-testid="form-project-type"
                  value={form.project_type}
                  onChange={set("project_type")}
                  className={inputCls}
                  required
                >
                  <option value="" disabled>Select a project</option>
                  {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Budget range">
                  <select
                    data-testid="form-budget"
                    value={form.budget_range}
                    onChange={set("budget_range")}
                    className={inputCls}
                  >
                    <option value="">Select budget</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="Timeline">
                  <select
                    data-testid="form-timeline"
                    value={form.timeline}
                    onChange={set("timeline")}
                    className={inputCls}
                  >
                    <option value="">Select timeline</option>
                    {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Tell us about your project">
                <textarea
                  data-testid="form-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Square footage, what you'd like changed, any inspiration photos you have, ideal start date…"
                  className={inputCls}
                />
              </Field>

              {/* Honeypot — hidden from humans, bots fill it and get silently dropped server-side */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0, overflow: "hidden" }}>
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set("website")}
                  />
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="estimate-form-submit"
                className="w-full group inline-flex items-center justify-center gap-3 bg-champagne text-[#0A0A0A] text-[12px] tracking-[0.22em] uppercase font-semibold px-7 py-4 rounded-sm hover:bg-[#D4AF37] disabled:opacity-60 disabled:cursor-not-allowed hover-lift"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request my free estimate
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <p className="text-[11px] tracking-wider text-[#525252] font-sans-body text-center">
                By submitting, you agree to be contacted by Cresta Bath & Tile. We never share your information.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.22em] uppercase text-[#A3A3A3] font-sans-body mb-2">{label}</span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <span className="mt-1 w-10 h-10 rounded-sm border border-[#262626] flex items-center justify-center bg-[#0C0C0C] group-hover:border-champagne transition-colors">
        <Icon className="w-4 h-4 text-champagne" />
      </span>
      <div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#A3A3A3] font-sans-body">{label}</div>
        <div className="mt-1 text-[#F5F5F0] font-sans-body">{value}</div>
      </div>
    </div>
  );
  return href ? <a data-testid={testid} href={href}>{inner}</a> : <div data-testid={testid}>{inner}</div>;
}
